import { z } from 'zod';
import { MoneyNum } from './numbers';

/* ───────────────────────── Справочники проекта ───────────────────────── */

export const PROJECT_STAGES = {
  preparation: 'Подготовка',
  tender: 'Тендер',
  design: 'Проектирование',
  expertise: 'Экспертиза',
  construction: 'Строительство',
  commissioning: 'Пусконаладка',
  warranty: 'Гарантия',
  closed: 'Закрыт',
} as const;
export type ProjectStage = keyof typeof PROJECT_STAGES;
export const ProjectStageSchema = z.enum(Object.keys(PROJECT_STAGES) as [ProjectStage, ...ProjectStage[]]);

export const OBJECT_STATES = {
  on_track: 'По графику',
  risk: 'Риск',
  issues: 'Замечания',
  not_started: 'Не начат',
} as const;
export type ObjectState = keyof typeof OBJECT_STATES;
export const ObjectStateSchema = z.enum(Object.keys(OBJECT_STATES) as [ObjectState, ...ObjectState[]]);

const DateStr = z.iso.date();
const Money = z.number().finite();
/** Ввод сумм — с пределом, чтобы ошибка ввода не превращалась в сбой сервера */
const MoneyIn = MoneyNum;

/* ───────────────────────── Контрагенты ───────────────────────── */

export const Counterparty = z.object({
  id: z.uuid(),
  name: z.string(),
  shortName: z.string().nullable(),
  bin: z.string().nullable(),
  isCustomer: z.boolean(),
  isSupplier: z.boolean(),
  isContractor: z.boolean(),
  isDesigner: z.boolean(),
  address: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  note: z.string().nullable(),
  isActive: z.boolean(),
});
export type Counterparty = z.infer<typeof Counterparty>;

export const CounterpartyInput = z.object({
  name: z.string().trim().min(2).max(300),
  shortName: z.string().trim().max(120).nullable().optional(),
  bin: z
    .string()
    .trim()
    .regex(/^\d{12}$/, 'БИН — 12 цифр')
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  isCustomer: z.boolean().optional(),
  isSupplier: z.boolean().optional(),
  isContractor: z.boolean().optional(),
  isDesigner: z.boolean().optional(),
  address: z.string().trim().max(500).nullable().optional(),
  phone: z.string().trim().max(100).nullable().optional(),
  email: z.string().trim().max(200).nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  isActive: z.boolean().optional(),
});
export type CounterpartyInput = z.infer<typeof CounterpartyInput>;

/* ───────────────────────── Направления (ПФ-12) ───────────────────────── */

export const Direction = z.object({
  id: z.uuid(),
  legalEntityId: z.uuid(),
  name: z.string(),
  code: z.string().nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
});
export type Direction = z.infer<typeof Direction>;

export const DirectionInput = z.object({
  legalEntityId: z.uuid(),
  name: z.string().trim().min(2).max(200),
  code: z.string().trim().max(30).nullable().optional(),
  sortOrder: z.number().int().optional(),
  isActive: z.boolean().optional(),
});
export type DirectionInput = z.infer<typeof DirectionInput>;

export const CURRENCIES = ['KZT', 'USD', 'EUR', 'RUB', 'CNY'] as const;

/* ───────────────────────── Проекты и объекты ───────────────────────── */

export const ProjectInput = z.object({
  legalEntityId: z.uuid(),
  directionId: z.uuid().nullable().optional(),
  /** Проект, в который входит подпроект (ПФ-11) */
  parentId: z.uuid().nullable().optional(),
  /** Площадка или программа, объединяющая проекты: «Карабатан» */
  siteName: z.string().trim().max(120).nullable().optional(),
  code: z.string().trim().min(1).max(50),
  name: z.string().trim().min(2).max(300),
  currency: z.enum(CURRENCIES).optional(),
  fullName: z.string().trim().max(1000).nullable().optional(),
  contractNumber: z.string().trim().max(100).nullable().optional(),
  contractDate: DateStr.nullable().optional(),
  customerId: z.uuid().nullable().optional(),
  stage: ProjectStageSchema.optional(),
  region: z.string().trim().max(200).nullable().optional(),
  address: z.string().trim().max(500).nullable().optional(),
  startDate: DateStr.nullable().optional(),
  finishDate: DateStr.nullable().optional(),
  contractValue: MoneyIn.nonnegative().nullable().optional(),
  managerUserId: z.uuid().nullable().optional(),
  description: z.string().trim().max(5000).nullable().optional(),
});
export type ProjectInput = z.infer<typeof ProjectInput>;

export const ObjectInput = z.object({
  parentId: z.uuid().nullable().optional(),
  code: z.string().trim().max(100).nullable().optional(),
  name: z.string().trim().min(2).max(300),
  stage: ProjectStageSchema.optional(),
  description: z.string().trim().max(5000).nullable().optional(),
  startDate: DateStr.nullable().optional(),
  finishDate: DateStr.nullable().optional(),
  estimateValue: MoneyIn.nonnegative().nullable().optional(),
  state: ObjectStateSchema.optional(),
  stateNote: z.string().trim().max(500).nullable().optional(),
  sortOrder: z.number().int().optional(),
});
export type ObjectInput = z.infer<typeof ObjectInput>;

/** Показатели считаются одной функцией на весь портал (общее правило раздела 6 ТЗ) */
export const Metrics = z.object({
  estimateValue: Money.nullable(),
  budgetTotal: Money.nullable(),
  budgetDistributed: Money.nullable(),
  planMarginPct: z.number().nullable(),
  forecastMarginPct: z.number().nullable(),
  planToDate: Money.nullable(),
  paidToDate: Money.nullable(),
  paidPctOfBudget: z.number().nullable(),
  scheduleDeviationDays: z.number().nullable(),
});
export type Metrics = z.infer<typeof Metrics>;

export const ProjectObject = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  parentId: z.uuid().nullable(),
  code: z.string().nullable(),
  name: z.string(),
  stage: ProjectStageSchema,
  description: z.string().nullable(),
  startDate: DateStr.nullable(),
  finishDate: DateStr.nullable(),
  estimateValue: Money.nullable(),
  state: ObjectStateSchema,
  stateNote: z.string().nullable(),
  sortOrder: z.number().int(),
  metrics: Metrics,
});
export type ProjectObject = z.infer<typeof ProjectObject>;

export const Milestone = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  objectId: z.uuid().nullable(),
  name: z.string(),
  kind: z.enum(['contract', 'internal']),
  baselineDate: DateStr,
  forecastDate: DateStr.nullable(),
  actualDate: DateStr.nullable(),
  deviationDays: z.number().int().nullable(),
  note: z.string().nullable(),
});
export type Milestone = z.infer<typeof Milestone>;

export const MilestoneInput = z.object({
  objectId: z.uuid().nullable().optional(),
  name: z.string().trim().min(2).max(300),
  kind: z.enum(['contract', 'internal']).optional(),
  baselineDate: DateStr,
  forecastDate: DateStr.nullable().optional(),
  actualDate: DateStr.nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type MilestoneInput = z.infer<typeof MilestoneInput>;

export const ProjectSummary = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  siteName: z.string().nullable(),
  parentId: z.uuid().nullable(),
  directionId: z.uuid().nullable(),
  directionName: z.string().nullable(),
  currency: z.string(),
  legalEntityId: z.uuid(),
  legalEntityName: z.string(),
  stage: ProjectStageSchema,
  customerName: z.string().nullable(),
  contractNumber: z.string().nullable(),
  contractValue: Money.nullable(),
  startDate: DateStr.nullable(),
  finishDate: DateStr.nullable(),
  region: z.string().nullable(),
  managerName: z.string().nullable(),
  status: z.enum(['draft', 'active', 'archived']),
  metrics: Metrics,
  objects: z.array(ProjectObject),
  openAttention: z.number().int(),
});
export type ProjectSummary = z.infer<typeof ProjectSummary>;

export const ProjectDetail = ProjectSummary.extend({
  fullName: z.string().nullable(),
  contractDate: DateStr.nullable(),
  customerId: z.uuid().nullable(),
  address: z.string().nullable(),
  managerUserId: z.uuid().nullable(),
  description: z.string().nullable(),
  milestones: z.array(Milestone),
});
export type ProjectDetail = z.infer<typeof ProjectDetail>;

/* ───────────────────────── Бюджет-график оплат (ПФ-04, ПФ-05) ───────────────────────── */

export const BudgetLine = z.object({
  id: z.uuid(),
  parentId: z.uuid().nullable(),
  objectId: z.uuid().nullable(),
  code: z.string().nullable(),
  name: z.string(),
  kind: z.enum(['section', 'item', 'alternative']),
  sectionCode: z.string().nullable(),
  estimateNo: z.string().nullable(),
  unit: z.string().nullable(),
  quantity: z.number().nullable(),
  materials: Money.nullable(),
  works: Money.nullable(),
  amount: Money,
  /** Сумма в бюджете с учётом выбранного варианта исполнения */
  effectiveAmount: Money,
  /** Вариант исполнения выбран вместо собственной суммы раздела (6.6) */
  isSelected: z.boolean(),
  distributed: Money,
  isDistributed: z.boolean(),
  periods: z.record(z.string(), Money),
});
export type BudgetLine = z.infer<typeof BudgetLine>;

export const BudgetVersion = z.object({
  id: z.uuid(),
  number: z.number().int(),
  name: z.string(),
  status: z.enum(['draft', 'approved', 'superseded']),
  approvedAt: z.string().nullable(),
  approvedBy: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
  total: Money,
  distributed: Money,
  canApprove: z.boolean(),
  approveBlockers: z.array(z.string()),
});
export type BudgetVersion = z.infer<typeof BudgetVersion>;

export const BudgetView = z.object({
  versions: z.array(BudgetVersion),
  current: BudgetVersion.nullable(),
  periods: z.array(z.object({ start: DateStr, label: z.string(), month: z.string() })),
  lines: z.array(BudgetLine),
});
export type BudgetView = z.infer<typeof BudgetView>;

export const PaymentFact = z.object({
  id: z.uuid(),
  objectId: z.uuid().nullable(),
  budgetLineId: z.uuid().nullable(),
  counterpartyName: z.string().nullable(),
  paidOn: DateStr,
  amount: Money,
  /** in — поступление от заказчика, out — оплата поставщику или расход */
  direction: z.enum(['in', 'out']),
  costArticle: z.string().nullable(),
  document: z.string().nullable(),
  source: z.string(),
  note: z.string().nullable(),
  /** Договор, по которому оплата внесена без заявки: закрывает его график платежей (ФН-02) */
  contractId: z.uuid().nullable(),
  contractNumber: z.string().nullable(),
});
export type PaymentFact = z.infer<typeof PaymentFact>;

export const PaymentFactInput = z.object({
  objectId: z.uuid().nullable().optional(),
  budgetLineId: z.uuid().nullable().optional(),
  counterpartyId: z.uuid().nullable().optional(),
  /** Оплата по расходному договору без заявки на оплату: контрагент и статья — из договора */
  contractId: z.uuid().nullable().optional(),
  paidOn: DateStr,
  amount: MoneyIn.positive('Сумма больше нуля'),
  direction: z.enum(['in', 'out']).default('out'),
  /** Одна из двенадцати статей расходов (БМ-14); для поступлений не нужна */
  costArticle: z.string().trim().max(200).nullable().optional(),
  document: z.string().trim().max(300).nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type PaymentFactInput = z.infer<typeof PaymentFactInput>;

export const CashflowPoint = z.object({
  start: DateStr,
  label: z.string(),
  plan: Money,
  fact: Money,
  planCumulative: Money,
  factCumulative: Money,
});
export type CashflowPoint = z.infer<typeof CashflowPoint>;

/* ───────────────────────── Требует внимания (ПФ-02) ───────────────────────── */

export const AttentionItem = z.object({
  id: z.string(),
  projectId: z.uuid().nullable(),
  projectCode: z.string().nullable(),
  severity: z.enum(['critical', 'warning', 'info']),
  title: z.string(),
  details: z.string().nullable(),
  sourceModule: z.string(),
  actionLabel: z.string().nullable(),
  actionPath: z.string().nullable(),
  computed: z.boolean(),
  createdAt: z.string().nullable(),
});
export type AttentionItem = z.infer<typeof AttentionItem>;

/* ───────────────────────── Портфель (ПФ-01) ───────────────────────── */

/** Строка свода на любом уровне: компания, направление, проект, подпроект (ПФ-14) */
export const StructureRow = z.object({
  level: z.enum(['company', 'direction', 'project', 'subproject']),
  id: z.string(),
  parentId: z.string().nullable(),
  name: z.string(),
  code: z.string().nullable(),
  stage: ProjectStageSchema.nullable(),
  projects: z.number().int(),
  subprojects: z.number().int(),
  metrics: Metrics,
  attention: z.number().int(),
  worstSeverity: z.enum(['critical', 'warning', 'info']).nullable(),
});
export type StructureRow = z.infer<typeof StructureRow>;

export const Portfolio = z.object({
  asOf: DateStr,
  moneyHidden: z.boolean(),
  /** Суммы видны только в части проектов (ДС-18): итоги по нескольким проектам скрыты */
  moneyPartial: z.boolean().optional(),
  kpi: z.object({
    projects: z.number().int(),
    objects: z.number().int(),
    estimateValue: Money.nullable(),
    contractValue: Money.nullable(),
    budgetTotal: Money.nullable(),
    planMarginPct: z.number().nullable(),
    forecastMarginPct: z.number().nullable(),
    paidToDate: Money.nullable(),
    planToDate: Money.nullable(),
    scheduleDeviationDays: z.number().nullable(),
    scheduleNote: z.string().nullable(),
    openAttention: z.number().int(),
  }),
  projects: z.array(ProjectSummary),
  structure: z.array(StructureRow),
  attention: z.array(AttentionItem),
  cashflow: z.array(CashflowPoint),
});
export type Portfolio = z.infer<typeof Portfolio>;

/* ───────────────────────── Импорт бюджета ───────────────────────── */

export const BudgetImportResult = z.object({
  versionId: z.uuid(),
  versionName: z.string(),
  sections: z.number().int(),
  items: z.number().int(),
  total: Money,
  distributed: Money,
  milestonesAdded: z.number().int(),
  issues: z.array(
    z.object({
      severity: z.enum(['critical', 'warning', 'info']),
      title: z.string(),
      details: z.string().nullable(),
    }),
  ),
});
export type BudgetImportResult = z.infer<typeof BudgetImportResult>;

/* ───────────────────────── Недели графика оплат ───────────────────────── */

const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

/** Подпись недели по её понедельнику: «окт, 3-я нед.» — как в Excel-бюджетах компании */
export function weekLabel(start: string): string {
  const [, m = 1, d = 1] = start.split('-').map(Number);
  return `${MONTHS_SHORT[m - 1] ?? ''}, ${Math.floor((d - 1) / 7) + 1}-я нед.`;
}

/** Понедельник недели, в которую попадает дата */
export function mondayOf(date: string): string {
  const t = new Date(`${date}T00:00:00Z`);
  const day = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - day);
  return t.toISOString().slice(0, 10);
}
